import { EventName } from "module/analytics/types"
import { AppNetwork } from "module/network/container/AppNetwork"
import { formatNumberPositionSize, formatNumberUSD } from "util/format"
import { formatTimeStamp } from "util/time"

import { Badge, Box, HStack, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react"
import { PositionHistory, PositionSide } from "@perp/sdk-curie"
import { MarketListIcon } from "component/MarketListIcon"
import { PerpLink } from "component/PerpLink"
import { PerpTable } from "component/PerpTable"
import { ContractFunctionSignature } from "constant/contractFunctionSignatures"
import { useTranslation } from "react-i18next"
import { PositionHistoryContainer } from "sdk-react/history/PositionHistoryContainer"

interface TradeHistoryTableRowProps {
    positionHistory: PositionHistory
}

const PositionBadge = ({ isLong }: { isLong: boolean }) => {
    const { t } = useTranslation()

    return (
        <Badge variant="subtle" colorScheme={isLong ? "green" : "red"}>
            {isLong ? t("position.side_long") : t("position.side_short")}
        </Badge>
    )
}

const getNote = (signature?: string) => {
    switch (signature) {
        case ContractFunctionSignature.OPEN_POSITION:
        case ContractFunctionSignature.OPEN_POSITION_FOR:
        case ContractFunctionSignature.CLOSE_POSITION: {
            return ""
        }
        case ContractFunctionSignature.FILL_LIMIT_ORDER: {
            return "Limit order filled."
        }
        case ContractFunctionSignature.ADD_LIQUIDITY: {
            // TODO: when useTakerBalance feature added to FE, this should also be reviewed again.
            return "Position converted to liquidity."
        }
        case ContractFunctionSignature.REMOVE_LIQUIDITY: {
            return "Permanent position from removed liquidity."
        }
        case ContractFunctionSignature.CANCEL_ALL_EXCESS_ORDERS:
        case ContractFunctionSignature.CANCEL_EXCESS_ORDERS: {
            return (
                <>
                    Permanent position from forcibly-removed liquidity.{" "}
                    <PerpLink
                        trackingProps={{ eventName: EventName.LINK_MAKER_LIQUIDATION }}
                        href="https://support.perp.com/hc/en-us/articles/6456586794649"
                    >
                        <Text as="span" color="perpetual.500">
                            Learn more ↗
                        </Text>
                    </PerpLink>
                </>
            )
        }
        case ContractFunctionSignature.LIQUIDATE:
        case ContractFunctionSignature.LIQUIDATE_V2:
        case ContractFunctionSignature.LIQUIDATE_V3: {
            return "Position liquidated."
        }
        default: {
            return "Third party contract calls."
        }
    }
}

export const TradeHistoryTableRow = ({ positionHistory }: TradeHistoryTableRowProps) => {
    const { txId, timestamp, baseSymbol, side, size, price, tradingFee, isClosed, fromFunctionSignature } =
        positionHistory
    const { explorerBuilder } = AppNetwork.useContainer()
    const link = explorerBuilder(txId, "transaction")

    return (
        <Tr>
            <Td textDecoration="underline">
                <PerpLink trackingProps={{ eventName: EventName.LINK_TRADER_HISTORY_TABLE_ROW_CLICK }} href={link}>
                    {formatTimeStamp(timestamp)}
                </PerpLink>
            </Td>
            <Td>{formatNumberPositionSize(size)}</Td>
            <Td>
                <HStack>
                    <Box boxSize="16px">
                        <MarketListIcon coin={baseSymbol} boxSize="16px" />
                    </Box>
                    <Text>{baseSymbol}</Text>
                    <PositionBadge isLong={side === PositionSide.LONG} />
                </HStack>
            </Td>
            <Td>
                {formatNumberUSD({ num: price })}
                {isClosed ? (
                    <>
                        <br />
                        {"(Closed Price)"}
                    </>
                ) : null}
            </Td>
            <Td>{formatNumberUSD({ num: positionHistory.realizedPnl })}</Td>
            <Td>{formatNumberUSD({ num: tradingFee })}</Td>
            <Td>{getNote(fromFunctionSignature)}</Td>
        </Tr>
    )
}

export const TradeHistoryTableHeaderRow = () => {
    const { t } = useTranslation()
    return (
        <Tr>
            <Th>{t("general.time")}</Th>
            <Th>{t("position.position_size")}</Th>
            <Th>{t("position.market_side")}</Th>
            <Th>{t("general.price")}</Th>
            <Th>{t("general.realized_profit_loss")}</Th>
            <Th>{t("general.transaction_fee")}</Th>
            <Th>{t("general.notes")}</Th>
        </Tr>
    )
}

export const TradeHistoryTable = () => {
    const { positionHistories, fetchNextPage, isLoading } = PositionHistoryContainer.useContainer()

    return (
        <PerpTable onLoadMore={fetchNextPage} isLoadingMore={isLoading}>
            <Thead>
                <TradeHistoryTableHeaderRow />
            </Thead>
            <Tbody>
                {positionHistories.map(history => {
                    return <TradeHistoryTableRow positionHistory={history} key={history.txId} />
                })}
            </Tbody>
        </PerpTable>
    )
}
